/**
 * Landing-to-checkout funnel events (BPEI plan, Track 6.1). One queue per tab, sent to
 * `POST /api/funnel/events` via sendBeacon (keepalive fetch fallback).
 *
 * Send timing (2026-09-14, load-speed root cause): events used to wait for 2 s of quiet.
 * A tab killed inside that window (an in-app browser dismissed without pagehide, a closed
 * test context) lost guide_landed, so a visit that DID load read as "never loaded" - four
 * fully rendered QA visits recorded html_arrived only. Stage events now go out on the next
 * tick (events fired together still share one beacon); only the chatty ones in BATCHED
 * keep the 2 s idle window. 20 events still flush at once. visitorId = the anonymous
 * ProgramVisit id, sessionId = per tab, userId once signed in; never PII in `meta`.
 * Stage keys mirror Nexus `services/funnel/funnel-stages.ts`.
 */
import { useVisitorId } from '@/composables/useVisitorId';
import { useAcquisitionMetadata } from '@/composables/useAcquisitionMetadata';
import { FEATURE_FLAGS } from '@/constants/featureFlags';

export type FunnelStage =
  | 'guide_landed'
  | 'join_clicked'
  | 'auth_contact_submitted'
  | 'auth_code_verified'
  | 'auth_completed'
  | 'start_form_completed'
  | 'start_program_clicked'
  | 'checkout_sheet_opened'
  | 'checkout_option_selected'
  | 'stripe_redirected'
  | 'checkout_paid';

export type FunnelEventName =
  | FunnelStage
  | 'hero_seen'
  | 'auth_step'
  | 'start_item_completed'
  | 'checkout_returned'
  | 'activity_opened'
  | 'activity_completed'
  | 'overview_seen'
  | 'landing_scrolled'
  | 'landing_dwell'
  | 'landing_visible'
  | 'landing_alive'
  | 'landing_touch'
  | 'landing_click'
  | 'landing_lang'
  | 'landing_translated'
  | 'timeline_tapped'
  | 'read_more'
  | 'call_clicked'
  | 'video_play'
  | 'share_clicked'
  | 'eligibility_notice'
  | 'qualify_step'
  | 'proof_jump'
  | 'client_error';

export interface FunnelEventInput {
  programId: string;
  event: FunnelEventName;
  step?: number;
  meta?: Record<string, unknown>;
  userId?: string | null;
}

interface QueuedEvent {
  programId: string;
  visitorId: string;
  sessionId: string;
  userId?: string;
  event: string;
  step?: number;
  meta?: Record<string, unknown>;
  utm?: { source?: string; medium?: string; campaign?: string; content?: string };
  referrer?: string;
  path?: string;
  device?: 'mobile' | 'desktop';
  viewportW?: number;
  ts: string;
}

const SESSION_KEY = 'pcv-funnel-session';
const FLUSH_IDLE_MS = 2000;
/** High-frequency events that may wait for quiet; everything else sends on the next tick. */
const BATCHED = new Set<string>(['landing_scrolled', 'activity_opened', 'client_error']);
/**
 * Events fired in the same click that navigates away (a Calendly or SMS link, a share, the Stripe
 * redirect) are sent synchronously: in in-app browsers the page unloads before a next-tick timer
 * runs and no pagehide fires. Measured 2026-09-15: a real tap on "Schedule an interview" arrived
 * as landing_touch (hit a:join-peek-join) with no call_clicked, then the visitor came back.
 */
const SEND_NOW = new Set<string>(['call_clicked', 'share_clicked', 'stripe_redirected']);
const MAX_BATCH = 20;
const OBJECT_ID = /^[a-f0-9]{24}$/i;

let queue: QueuedEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let listenersBound = false;
const sentOnce = new Set<string>();

function baseUrl(): string {
  const env = (import.meta as any).env || {};
  return String(
    env.VITE_NEXUS_REST || env.VITE_NEXUS_API || 'https://services.pilotcity.com'
  ).replace(/\/$/, '');
}

function enabled(): boolean {
  return FEATURE_FLAGS.FUNNEL_ANALYTICS !== false;
}

// Resolved once per page load for the same reason as the visitor id: storage reads
// at unload time came back empty and the closing beacon carried a new session id.
let resolvedSessionId: string | null = null;

function sessionId(): string {
  if (resolvedSessionId) return resolvedSessionId;
  try {
    const existing = window.sessionStorage.getItem(SESSION_KEY);
    if (existing) {
      resolvedSessionId = existing;
      return existing;
    }
    const fresh =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    window.sessionStorage.setItem(SESSION_KEY, fresh);
    resolvedSessionId = fresh;
    return fresh;
  } catch {
    resolvedSessionId = `mem-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    return resolvedSessionId;
  }
}

/**
 * Ad platforms tag clicks with their own ids instead of utm (Google Ads gclid/gad_source,
 * Meta fbclid, TikTok ttclid, Microsoft msclkid). Read them as a source so paid traffic
 * is never filed under "direct".
 */
export function utmFromSearch(search: string): QueuedEvent['utm'] | undefined {
  const p = new URLSearchParams(search);
  const utm: NonNullable<QueuedEvent['utm']> = {
    source: p.get('utm_source') || undefined,
    medium: p.get('utm_medium') || undefined,
    campaign: p.get('utm_campaign') || undefined,
    content: p.get('utm_content') || undefined
  };
  if (!utm.source) {
    if (p.get('gclid') || p.get('gad_source') || p.get('gbraid') || p.get('wbraid')) {
      utm.source = 'google';
      utm.medium = utm.medium || 'cpc';
      utm.campaign = utm.campaign || p.get('gad_campaignid') || undefined;
    } else if (p.get('fbclid')) {
      utm.source = 'facebook';
      utm.medium = utm.medium || 'cpc';
    } else if (p.get('ttclid')) {
      utm.source = 'tiktok';
      utm.medium = utm.medium || 'cpc';
    } else if (p.get('msclkid')) {
      utm.source = 'bing';
      utm.medium = utm.medium || 'cpc';
    }
  }
  return Object.values(utm).some(Boolean) ? utm : undefined;
}

function utmFromLocation(): QueuedEvent['utm'] | undefined {
  try {
    return utmFromSearch(window.location.search);
  } catch {
    return undefined;
  }
}

// utm/referrer only exist on the landing URL; remember them for the rest of the tab.
let landingUtm: QueuedEvent['utm'] | undefined;
let landingReferrer: string | undefined;

// Experiment context (exp + variant) merged into every event's meta once set, so the
// summary can split any stage by variant. Cleared with an empty object.
let funnelContext: Record<string, unknown> = {};
export function setFunnelContext(ctx: Record<string, unknown>): void {
  funnelContext = { ...ctx };
}
export function getFunnelContext(): Record<string, unknown> {
  return { ...funnelContext };
}

function send(events: QueuedEvent[]): void {
  if (!events.length) return;
  const url = `${baseUrl()}/api/funnel/events`;
  const body = JSON.stringify({ events });
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      // text/plain keeps sendBeacon simple-request safe; the server accepts both.
      if (navigator.sendBeacon(url, new Blob([body], { type: 'text/plain' }))) return;
    }
  } catch {
    /* fall through to fetch */
  }
  try {
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      credentials: 'omit'
    }).catch(() => undefined);
  } catch {
    /* analytics must never throw into the app */
  }
}

export function flushFunnelEvents(): void {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  const batch = queue;
  queue = [];
  send(batch);
}

function bindLifecycle(): void {
  if (listenersBound || typeof window === 'undefined') return;
  listenersBound = true;
  const onHide = () => {
    if (document.visibilityState === 'hidden') flushFunnelEvents();
  };
  document.addEventListener('visibilitychange', onHide);
  window.addEventListener('pagehide', flushFunnelEvents);
}

export function useFunnelTracker() {
  const { getOrCreateVisitorId } = useVisitorId();
  const { collect } = useAcquisitionMetadata();

  /**
   * Queue one event. `once` dedupes per tab (e.g. guide_landed, hero_seen).
   */
  const track = (input: FunnelEventInput, options: { once?: boolean } = {}): void => {
    try {
      if (!enabled()) return;
      if (!input.programId || !OBJECT_ID.test(input.programId)) return;
      if (typeof window === 'undefined') return;
      const key = `${input.programId}:${input.event}:${input.step ?? ''}`;
      if (options.once) {
        if (sentOnce.has(key)) return;
        sentOnce.add(key);
      }
      if (!landingUtm) landingUtm = utmFromLocation();
      if (!landingReferrer) {
        const meta = collect();
        landingReferrer = meta.referrer || undefined;
      }
      const ev: QueuedEvent = {
        programId: input.programId,
        visitorId: getOrCreateVisitorId(),
        sessionId: sessionId(),
        userId: input.userId && OBJECT_ID.test(input.userId) ? input.userId : undefined,
        event: input.event,
        step: typeof input.step === 'number' ? input.step : undefined,
        meta:
          Object.keys(funnelContext).length || input.meta
            ? { ...funnelContext, ...(input.meta || {}) }
            : undefined,
        utm: landingUtm,
        referrer: landingReferrer,
        path: `${window.location.pathname}${window.location.search}`.slice(0, 512),
        device: window.innerWidth < 768 ? 'mobile' : 'desktop',
        viewportW: window.innerWidth,
        ts: new Date().toISOString()
      };
      queue.push(ev);
      bindLifecycle();
      if (queue.length >= MAX_BATCH || SEND_NOW.has(ev.event)) {
        flushFunnelEvents();
        return;
      }
      if (timer) clearTimeout(timer);
      const batched = queue.every(q => BATCHED.has(q.event));
      timer = setTimeout(flushFunnelEvents, batched ? FLUSH_IDLE_MS : 0);
    } catch {
      /* never let analytics break the flow */
    }
  };

  return { track, flush: flushFunnelEvents };
}
